<script setup lang="ts">
import WordleKeyboard from "@/components/wordle/WordleKeyboard.vue";
import TopNavigation from "@/components/TopNavigation.vue";
import WordleBoard from "@/components/wordle/WordleBoard.vue";
import { useWordle6Store } from "@/stores/modules/wordle6/wordle6";
import { onMounted, onUnmounted, ref, watch } from "vue";
import WordleEndGameStats from "@/components/wordle/WordleEndGameStats.vue";
import Wordle6TodayLeaderBoard from "@/views/games/wordle6/components/Wordle6TodayLeaderBoard.vue";
import Wordle6TotalLeaderBoard from "@/views/games/wordle6/components/Wordle6TotalLeaderBoard.vue";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import { useI18n } from "vue-i18n";
import { InformationCircleIcon } from "@heroicons/vue/outline";
import { CalendarIcon } from "@heroicons/vue/solid";
import InformationModal from "@/components/Modals/InformationModal.vue";
import Wordle6Logo from "@/views/games/wordle6/components/Wordle6Logo.vue";
import Wordle6MonthlyLeaderBoard from "@/views/games/wordle6/components/Wordle6MonthlyLeaderBoard.vue";
import Ping from "@/components/Ping.vue";
import MedalIcon from "@/components/Icons/MedalIcon.vue";
import LeaderboardIcon from "@/components/Icons/LeaderboardIcon.vue";
import { setLocalStorageItem } from "@/helpers/utils/local-storage";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";
import InformationText from "@/components/InformationText.vue";

const { t } = useI18n({ useScope: "global" });
const wordle6Store = useWordle6Store();

wordle6Store.initWordle();

const darkModeStore = useDarkModeStore();
const darkModeEnabled = darkModeStore.darkModeEnabled;

const viewStates = ref({
  board: "board",
  leaderboard: "leaderboard",
  statistics: "statistics",
  monthlyScores: "monthly-scores",
});

const viewState = ref(viewStates.value.board);
const showInformationModal = ref(false);

watch(
  () => viewState.value,
  () => {
    wordle6Store.fetchGameState({ force: true });
  },
);

function keyClicked(key: string): void {
  wordle6Store.keyPressed(key);
}

const hasNotSeenInformation = ref(false);
const hasNotSeenMonthlyScore = ref(false);
let refreshInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  window.scrollTo(0, 0);
  if (!localStorage.getItem("wordle6HasNotSeenInformation")) {
    hasNotSeenInformation.value = true;
  }
  if (!localStorage.getItem("wordle6HasNotSeenMonthlyScore")) {
    hasNotSeenMonthlyScore.value = true;
  }

  refreshInterval = setInterval(() => {
    if (wordle6Store.hasFinished) {
      if (refreshInterval) clearInterval(refreshInterval);
    }
    if (wordle6Store.matchId) {
      wordle6Store.fetchGameState({ matchId: wordle6Store.matchId });
    } else {
      wordle6Store.fetchGameState();
    }
  }, 4000);
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});

function setHasSeenMonthlyScore(): void {
  hasNotSeenMonthlyScore.value = false;
  setLocalStorageItem("wordle6HasNotSeenMonthlyScore", "true");
}

function setHasSeenInformation(): void {
  hasNotSeenInformation.value = false;
  setLocalStorageItem("wordle6HasNotSeenInformation", "true");
}

const { maybePromptReview } = useAppReviewPrompt();

watch(
  () => wordle6Store.hasFinished,
  (finished) => {
    if (finished) {
      maybePromptReview();
    }
  },
);
</script>
<template>
  <information-modal
    :visible="showInformationModal"
    @close="showInformationModal = false"
  >
    <div
      class="prose text-kekkle-dark dark:text-white dark:prose-invert"
      v-html="t('pages.wordle6.additional_information')"
    ></div>
  </information-modal>
  <div class="min-h-screen pb-4">
    <top-navigation
      :title="t('pages.games.wordle6_title')"
      button-name="button"
    />
    <top-navigation
      class="relative opacity-0"
      :title="t('pages.games.wordle6_title')"
      button-name="button"
    />

    <div
      class="my-2 flex justify-between gap-4 border-b-2 border-gray-400 p-2 pt-0"
    >
      <div class="flex items-center gap-x-4">
        <button @click="viewState = viewStates.board">
          <Wordle6Logo />
        </button>
        <div
          class="relative"
          @click="
            () => {
              showInformationModal = true;
              setHasSeenInformation();
            }
          "
        >
          <InformationCircleIcon
            class="relative h-6"
            :class="darkModeEnabled ? 'text-white' : 'text-gray-600'"
          ></InformationCircleIcon>
          <ping v-if="hasNotSeenInformation"></ping>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-4">
        <p @click="viewState = viewStates.leaderboard">
          <MedalIcon class="h-7 w-7 dark:fill-white"></MedalIcon>
        </p>
        <p
          class="relative"
          @click="
            () => {
              viewState = viewStates.monthlyScores;
              setHasSeenMonthlyScore();
            }
          "
        >
          <CalendarIcon class="h-8 dark:text-white"></CalendarIcon>
          <ping v-if="hasNotSeenMonthlyScore"></ping>
        </p>
        <p @click="viewState = viewStates.statistics">
          <leaderboard-icon class="h-8 w-8 dark:fill-white"></leaderboard-icon>
        </p>
      </div>
    </div>
    <div class="mx-auto max-w-md">
      <InformationText
        v-if="wordle6Store.fetchError"
        color="red"
        class="mx-2 mb-2"
        :information="wordle6Store.fetchError"
      />
      <template v-if="viewState === viewStates.leaderboard">
        <Wordle6TotalLeaderBoard />
      </template>
      <template v-if="viewState === viewStates.monthlyScores">
        <Wordle6MonthlyLeaderBoard />
      </template>
      <template v-if="viewState === viewStates.statistics">
        <Wordle6TodayLeaderBoard />
      </template>
      <transition name="list">
        <div v-if="viewState === viewStates.board && wordle6Store.matchId">
          <h1 class="mb-2 text-center text-lg font-medium text-gray-500">
            {{ wordle6Store.matchDate?.toLocaleDateString() }}
          </h1>
          <WordleBoard
            :game-state="wordle6Store.gameState"
            :invalid-word="wordle6Store.invalidWord"
            :current-row="wordle6Store.currentRow"
          />
          <div v-if="!wordle6Store.hasFinished">
            <WordleKeyboard
              class="mt-4"
              :correct-characters="wordle6Store.correctCharacters"
              :present-characters="wordle6Store.presentCharacters"
              :wrong-characters="wordle6Store.wrongCharacters"
              @click-key="keyClicked"
            />
          </div>
          <WordleEndGameStats
            v-else
            class="mt-4"
            :has-won="wordle6Store.hasWon"
            :correct-word="wordle6Store.correctWord ?? ''"
            :calculated-score="wordle6Store.calculatedScore"
          />
        </div>
      </transition>
    </div>
  </div>
</template>
<style scoped>
.list-enter-active {
  transition: all 0.5s ease;
}
.list-leave-active {
  transition: none;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
